import React, { useState, useEffect, useCallback } from 'react';
import { X, Building2, User, Mail, Phone, MapPin, Package, DollarSign, Calendar, Loader2, AlertCircle, CheckCircle, XCircle, History } from 'lucide-react';
import { Retailer, Order } from '../../types';
import { updateRetailerStatus } from '../../services/retailerService';
import { getOrdersByRetailer } from '../../services/orderService';

interface RetailerDetailModalProps {
  retailer: Retailer;
  onClose: () => void;
  onUpdate: () => void;
}

const RetailerDetailModal: React.FC<RetailerDetailModalProps> = ({ retailer, onClose, onUpdate }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);

  const loadOrders = useCallback(async () => {
    setIsLoadingOrders(true);
    try {
      const data = await getOrdersByRetailer(retailer.id);
      setOrders(data);
    } catch (err) {
      console.error('Failed to load orders:', err);
    } finally {
      setIsLoadingOrders(false);
    }
  }, [retailer.id]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isUpdating) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, isUpdating]);

  const handleToggleStatus = async () => {
    setError('');
    setIsUpdating(true);

    const newStatus = retailer.status === 'active' ? 'suspended' : 'active';

    try {
      await updateRetailerStatus(retailer.id, newStatus);
      onUpdate();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update status');
    } finally {
      setIsUpdating(false);
    }
  };

  const getOrderStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'text-olive';
      case 'shipped': return 'text-ember-deep';
      case 'processing': return 'text-steel';
      case 'payment_received': return 'text-olive';
      default: return 'text-ink-700';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-forge-deep/70 backdrop-blur-sm" onClick={onClose}></div>

      <div className="relative bg-paper-bright shadow-float w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-forge text-paper px-6 py-5 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center text-paper/70 hover:text-paper hover:bg-paper/10 transition-colors duration-150"
            aria-label="Close"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-ember flex items-center justify-center flex-shrink-0">
              <Building2 className="w-5 h-5 text-paper-warm" strokeWidth={1.5} />
            </div>
            <div>
              <p className="font-mono text-[11px] tracking-micro uppercase text-ember-soft mb-1">— Retailer</p>
              <h2 className="font-editorial italic font-light text-2xl text-paper-warm tracking-tight">
                {retailer.businessName}.
              </h2>
              <p className="text-paper/70 text-sm mt-1 flex items-center gap-2">
                {retailer.status === 'active' ? (
                  <span className="flex items-center gap-1 text-olive">
                    <CheckCircle className="w-3 h-3" strokeWidth={1.5} /> Active
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-ember-soft">
                    <XCircle className="w-3 h-3" strokeWidth={1.5} /> Suspended
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 lg:p-8 overflow-y-auto max-h-[60vh]">
          {error && (
            <div className="mb-5 bg-ember-soft/25 border-l-2 border-ember p-3 flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-ember flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <p className="text-ember-deep text-sm">{error}</p>
            </div>
          )}

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-paper border border-ink-200 p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-ember/10 flex items-center justify-center">
                <Package className="w-5 h-5 text-ember" strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-0.5">Total orders</p>
                <p className="font-editorial italic text-xl text-ink-950">{retailer.totalOrders}</p>
              </div>
            </div>
            <div className="bg-paper border border-ink-200 p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-olive/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-olive" strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-0.5">Total spent</p>
                <p className="font-editorial italic text-xl text-ink-950">${retailer.totalSpent.toLocaleString()}</p>
              </div>
            </div>
            <div className="bg-paper border border-ink-200 p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-steel/10 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-steel" strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-0.5">Member since</p>
                <p className="text-sm font-medium text-ink-950">
                  {retailer.createdAt.toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Contact Info */}
            <div className="space-y-4">
              <h3 className="font-editorial italic text-lg text-ink-950 tracking-tight flex items-center gap-2">
                <User className="w-4 h-4 text-ember" strokeWidth={1.5} />
                Contact information
              </h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1">Contact name</p>
                  <p className="font-medium text-ink-950">{retailer.contactName}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-ink-500" strokeWidth={1.5} />
                  <p className="text-ink-900">{retailer.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-ink-500" strokeWidth={1.5} />
                  <p className="text-ink-900">{retailer.phone}</p>
                </div>
                <div>
                  <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1">Tax ID / HST</p>
                  <p className="font-medium font-mono text-ink-950">{retailer.taxId}</p>
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="space-y-4">
              <h3 className="font-editorial italic text-lg text-ink-950 tracking-tight flex items-center gap-2">
                <MapPin className="w-4 h-4 text-ember" strokeWidth={1.5} />
                Business address
              </h3>
              <div className="bg-paper border border-ink-200 p-4 text-sm text-ink-900">
                <p>{retailer.address.street}</p>
                <p>{retailer.address.city}, {retailer.address.province} {retailer.address.postalCode}</p>
                <p>{retailer.address.country}</p>
              </div>
            </div>
          </div>

          {/* Order History */}
          <div className="mt-6">
            <h3 className="font-editorial italic text-lg text-ink-950 tracking-tight flex items-center gap-2 mb-4">
              <History className="w-4 h-4 text-ember" strokeWidth={1.5} />
              Recent orders
            </h3>
            {isLoadingOrders ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-ink-500" strokeWidth={1.5} />
              </div>
            ) : orders.length === 0 ? (
              <div className="bg-paper border border-ink-200 p-6 text-center text-ink-500 text-sm">
                No orders yet
              </div>
            ) : (
              <div className="border border-ink-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-paper">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-mono text-[10px] tracking-micro uppercase text-ink-500">Order #</th>
                      <th className="px-4 py-2.5 text-left font-mono text-[10px] tracking-micro uppercase text-ink-500">Date</th>
                      <th className="px-4 py-2.5 text-left font-mono text-[10px] tracking-micro uppercase text-ink-500">Total</th>
                      <th className="px-4 py-2.5 text-left font-mono text-[10px] tracking-micro uppercase text-ink-500">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-200">
                    {orders.slice(0, 5).map((order) => (
                      <tr key={order.id} className="bg-paper-bright">
                        <td className="px-4 py-2.5 font-mono text-ink-950">{order.orderNumber}</td>
                        <td className="px-4 py-2.5 text-ink-700">
                          {order.createdAt.toLocaleDateString()}
                        </td>
                        <td className="px-4 py-2.5 font-medium text-ink-950">${order.total.toFixed(2)}</td>
                        <td className={`px-4 py-2.5 font-medium capitalize ${getOrderStatusColor(order.status)}`}>
                          {order.status.replace('_', ' ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 lg:px-8 py-5 border-t border-ink-200 bg-paper flex gap-3 justify-end">
          <button
            onClick={handleToggleStatus}
            disabled={isUpdating}
            className={`px-5 py-2.5 text-sm font-medium transition-colors duration-150 flex items-center gap-2 disabled:opacity-50 ${
              retailer.status === 'active'
                ? 'text-ember hover:bg-ember-soft/25'
                : 'text-olive hover:bg-olive/10'
            }`}
          >
            {isUpdating ? (
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
            ) : retailer.status === 'active' ? (
              <>
                <XCircle className="w-4 h-4" strokeWidth={1.5} />
                Suspend account
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" strokeWidth={1.5} />
                Reactivate account
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RetailerDetailModal;
